import { database } from './core';
import {
  ref,
  push,
  set,
  update,
  remove,
  get,
  query,
  orderByChild,
  equalTo,
  onValue
} from 'firebase/database';
import { getCurrentUser } from './auth.service';
import { checkAchievements, displayEarnedAchievements } from './achievementTriggers.service';
import { createWithKey } from './api-wrapper.service';
import logger from '../../utils/logger';
import { cleanForFirebase } from '../../utils/firebase';

// Competition type definition
export type CompetitionType = 'bjj' | 'judo' | 'wrestling' | 'mma' | 'boxing' | 'kickboxing' | 'other';

export interface Competition {
  id?: string;
  owner: string; // Note: original app uses 'owner' not 'userId'
  tournamentName: string; // Original app uses tournamentName, not name
  location?: {
    placeID?: string;
    name?: string;
    address?: string;
    country?: string;
    locality?: string;
    latitude?: number;
    longitude?: number;
    countryISOAlpha2?: string;
  }; // Original app stores location as object
  date: number; // timestamp
  dateCreated: number; // timestamp
  dateUpdated?: number; // timestamp
  timezoneOffset?: number;
  type?: CompetitionType;
  gi?: boolean; // Original app uses boolean
  trainedStyle?: string; // Original app field - martial art style
  description?: string;
  website?: string;
  note?: string; // Original app uses 'note' not 'notes'
  isRegistered?: boolean;
  isCompleted?: boolean;
  divisions?: string[];
  division?: string; // Specific division competed in
  weightClass?: string;
  // Original app structure for wins/losses/medals
  winsCount?: number; // Original app uses winsCount, not wins
  lossesCount?: number; // Original app uses lossesCount, not losses
  submissionWins?: number; // Submission wins
  submissionLosses?: number; // Submission losses
  rank?: 0 | 1 | 2 | 3; // Original app uses rank: 0=none, 1=gold, 2=silver, 3=bronze
  rounds?: Array<{
    status: 'won' | 'lost' | 'tie'; // Original app includes 'tie' for competition rounds
    opponentName?: string;
  }>; // Original app field
  taggedFriends?: Record<string, any>; // Original app field
  // Media - original app stores photos as OBJECT with numeric keys (like trainings)
  photos?: Record<string, {
    url: string;
    aspectRatio: number;
    dateAdded?: number;
    caption?: string;
  }>; // Original app format: { "0": {...}, "1": {...} }
  competitionPhoto?: {  // Legacy single photo (object with url and aspectRatio)
    url: string;
    aspectRatio?: number;
  };
  photoUrl?: string; // @deprecated - use competitionPhoto.url
  // Social features
  likes?: Record<string, boolean>;
  likesCount?: number;  // Original app uses plural 'likesCount'
  commentsCount?: number;  // Original app uses plural 'commentsCount'
  shownOnFeeds?: boolean;
}

export interface Match {
  id?: string;
  competitionId: string;
  owner: string; // User ID
  opponent?: string;
  date: number; // timestamp
  dateCreated: number;
  dateUpdated?: number;
  division?: string;
  weightClass?: string;
  result?: 'win' | 'loss' | 'draw';
  winMethod?: string;
  notes?: string;
  points?: {
    scored: number;
    against: number;
  };
}

/**
 * Creates a new competition
 * @param competitionData The competition data to create
 * @returns The ID of the newly created competition
 */
export const createCompetition = async (competitionData: Partial<Competition>): Promise<string> => {
  try {
    const user = getCurrentUser();
    if (!user) {
      throw new Error('You must be logged in to create a competition');
    }

    const now = Date.now();
    const timezoneOffset = new Date().getTimezoneOffset() / 60;

    // Build competition object exactly as original app does
    // Clean competitionData to remove undefined values before writing to Firebase
    const competition: any = {
      ...cleanForFirebase(competitionData),
      owner: user.uid, // Original app uses 'owner' field
      dateCreated: competitionData.dateCreated || now
    };

    // Only set timezoneOffset if not already provided
    if (!competition.timezoneOffset && competition.timezoneOffset !== 0) {
      competition.timezoneOffset = timezoneOffset;
    }

    // Create competition record using createWithKey - same as trainings
    logger.info('[competition.service] Creating competition in Firebase...');
    const competitionId = await createWithKey('/competitions', competition);
    
    if (!competitionId) {
      throw new Error('Failed to generate competition ID');
    }
    
    logger.info('[competition.service] Competition created with ID:', competitionId);
    
    // Check for achievements
    try {
      const earnedAchievements = await checkAchievements('competition_added', {
        id: competitionId,
        competitionData: competition
      });
      displayEarnedAchievements(earnedAchievements);
    } catch (achievementError) {
      logger.error('Error checking achievements:', achievementError);
    }

    logger.info('[competition.service] Competition created, cloud function will update feeds');
    return competitionId;
  } catch (error) {
    logger.error('Error creating competition:', error);
    throw error;
  }
};

/**
 * Updates an existing competition
 * @param competitionId The ID of the competition to update
 * @param competitionData The competition data to update
 */
export const updateCompetition = async (
  competitionId: string, 
  competitionData: Partial<Competition>
): Promise<void> => {
  try {
    const user = getCurrentUser();
    if (!user) {
      throw new Error('You must be logged in to update a competition');
    }

    const timezoneOffset = new Date().getTimezoneOffset() / 60;

    // Clean competitionData to remove undefined values before writing to Firebase
    const updates: Partial<Competition> = {
      ...cleanForFirebase(competitionData),
      dateUpdated: Date.now(),
      timezoneOffset,
    };

    const competitionRef = ref(database, `competitions/${competitionId}`);
    await update(competitionRef, updates);
    
    logger.info('[competition.service] Competition updated');
  } catch (error) {
    logger.error('Error updating competition:', error);
    throw error;
  }
};

/**
 * Deletes a competition
 * @param competitionId The ID of the competition to delete
 */
export const deleteCompetition = async (competitionId: string): Promise<void> => {
  try {
    const user = getCurrentUser();
    if (!user) {
      throw new Error('You must be logged in to delete a competition');
    }

    // Get competition to verify ownership and clean up photos
    const competition = await getCompetition(competitionId);
    if (!competition) {
      throw new Error('Competition not found');
    }

    // CRITICAL SECURITY CHECK: Only allow users to delete their own competitions
    if (competition.owner !== user.uid) {
      throw new Error('Unauthorized: You can only delete your own competitions');
    }

    // Delete any associated photos from Firebase Storage
    if (competition.photos) {
      const { ref: storageRef, deleteObject } = await import('firebase/storage');
      const { storage } = await import('./core');

      for (const photo of Object.values(competition.photos)) {
        try {
          // Extract file path from URL
          const url = new URL(photo.url);
          const path = decodeURIComponent(url.pathname.split('/o/')[1].split('?')[0]);
          const fileRef = storageRef(storage, path);
          await deleteObject(fileRef);
        } catch (photoError) {
          logger.warn(`Error deleting photo for competition ${competitionId}:`, photoError);
          // Continue with other deletions even if some photos fail
        }
      }
    }

    // Delete legacy competitionPhoto if exists
    if (competition.competitionPhoto?.url) {
      try {
        const { ref: storageRef, deleteObject } = await import('firebase/storage');
        const { storage } = await import('./core');

        const url = new URL(competition.competitionPhoto.url);
        const path = decodeURIComponent(url.pathname.split('/o/')[1].split('?')[0]);
        const fileRef = storageRef(storage, path);
        await deleteObject(fileRef);
      } catch (photoError) {
        logger.warn(`Error deleting competitionPhoto for ${competitionId}:`, photoError);
      }
    }

    // Delete the competition record
    const competitionRef = ref(database, `competitions/${competitionId}`);
    await remove(competitionRef);

    // Clean up feed items (fallback in case cloud function doesn't trigger)
    // This removes the competition from owner's feed and all friends' feeds
    try {
      const ownerId = competition.owner;

      // Get all accepted friends
      const friendsRef = ref(database, `users/${ownerId}/friends`);
      const friendsSnapshot = await get(friendsRef);
      const affectedUsers = [ownerId];

      if (friendsSnapshot.exists()) {
        const friends = friendsSnapshot.val();
        Object.keys(friends).forEach(friendId => {
          if (friends[friendId] === 'accepted') {
            affectedUsers.push(friendId);
          }
        });
      }

      // Remove feed items for all affected users
      const feedUpdates: Record<string, null> = {};
      affectedUsers.forEach(uid => {
        feedUpdates[`${uid}/${competitionId}`] = null;
      });

      const feedRef = ref(database, 'feeds');
      await update(feedRef, feedUpdates);

      logger.info(`[competition.service] Cleaned up feed items for ${affectedUsers.length} users`);
    } catch (feedCleanupError) {
      // Log but don't throw - competition is already deleted
      logger.warn('[competition.service] Feed cleanup warning:', feedCleanupError);
    }

    logger.info('[competition.service] Competition deleted:', competitionId);
  } catch (error) {
    logger.error('Error deleting competition:', error);
    throw error;
  }
};

/**
 * Gets all competitions for a user
 * @param uid User ID (defaults to current user)
 * @returns Array of competitions
 */
export const getUserCompetitions = async (uid?: string): Promise<Competition[]> => {
  try {
    const targetUid = uid || getCurrentUser()?.uid;
    if (!targetUid) {
      throw new Error('No user ID provided');
    }

    // Query competitions by owner field - matches original app
    const competitionsRef = ref(database, 'competitions');
    const competitionsQuery = query(
      competitionsRef,
      orderByChild('owner'),
      equalTo(targetUid)
    );

    const snapshot = await get(competitionsQuery);
    
    if (!snapshot.exists()) {
      return [];
    }

    const competitions: Competition[] = [];
    snapshot.forEach((childSnapshot) => {
      competitions.push({
        id: childSnapshot.key!,
        ...childSnapshot.val()
      });
    });

    // Sort by date descending (newest first)
    competitions.sort((a, b) => (b.date || 0) - (a.date || 0));

    return competitions;
  } catch (error) {
    logger.error('Error getting competitions:', error);
    throw error;
  }
};

/**
 * Subscribe to user competitions with real-time updates
 * @param uid User ID (defaults to current user)
 * @param callback Callback function when competitions change
 * @returns Unsubscribe function
 */
export const subscribeToUserCompetitions = (
  uid: string | undefined,
  callback: (competitions: Competition[]) => void
): (() => void) => {
  try {
    const targetUid = uid || getCurrentUser()?.uid;
    if (!targetUid) {
      logger.error('No user ID provided for competition subscription');
      callback([]);
      return () => {};
    }

    const competitionsRef = ref(database, 'competitions');
    const competitionsQuery = query(
      competitionsRef,
      orderByChild('owner'),
      equalTo(targetUid)
    );

    const unsubscribe = onValue(competitionsQuery, (snapshot) => {
      const competitions: Competition[] = [];
      
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          competitions.push({
            id: childSnapshot.key!,
            ...childSnapshot.val()
          });
        });
      }

      // Sort by date descending (newest first)
      competitions.sort((a, b) => (b.date || 0) - (a.date || 0));
      
      callback(competitions);
    }, (error) => {
      logger.error('Error in competition subscription:', error);
      callback([]);
    });

    return unsubscribe;
  } catch (error) {
    logger.error('Error setting up competition subscription:', error);
    return () => {};
  }
};

/**
 * Gets a single competition by ID
 * @param competitionId Competition ID
 * @returns Competition data or null
 */
export const getCompetition = async (competitionId: string): Promise<Competition | null> => {
  try {
    const competitionRef = ref(database, `competitions/${competitionId}`);
    const snapshot = await get(competitionRef);
    
    if (!snapshot.exists()) {
      return null;
    }

    return {
      id: competitionId,
      ...snapshot.val()
    };
  } catch (error) {
    logger.error('Error getting competition:', error);
    throw error;
  }
};

/**
 * Creates a match within a competition
 * @param competitionId Competition ID
 * @param matchData Match data
 * @returns Match ID
 */
export const createMatch = async (
  competitionId: string,
  matchData: Partial<Match>
): Promise<string> => {
  try {
    const user = getCurrentUser();
    if (!user) {
      throw new Error('Must be logged in to create a match');
    }

    // Matches are stored under competitions
    const matchesRef = ref(database, `competitions/${competitionId}/matches`);
    const newMatchRef = push(matchesRef);
    const matchId = newMatchRef.key;

    if (!matchId) {
      throw new Error('Failed to generate match ID');
    }

    // Clean matchData to remove undefined values before writing to Firebase
    const match: Match = {
      ...cleanForFirebase(matchData),
      competitionId,
      owner: user.uid,
      dateCreated: Date.now(),
    } as Match;

    await set(newMatchRef, match);
    
    // Update competition stats if match has result
    if (matchData.result) {
      await updateCompetitionStats(competitionId, matchData.result);
    }

    return matchId;
  } catch (error) {
    logger.error('Error creating match:', error);
    throw error;
  }
};

/**
 * Updates competition statistics based on match results
 * @param competitionId Competition ID
 * @param result Match result
 */
const updateCompetitionStats = async (
  competitionId: string,
  result: 'win' | 'loss' | 'draw'
): Promise<void> => {
  try {
    const competitionRef = ref(database, `competitions/${competitionId}`);
    const snapshot = await get(competitionRef);
    
    if (!snapshot.exists()) {
      logger.error('Competition not found for stats update');
      return;
    }

    const competition = snapshot.val();
    const currentStats = competition.stats || { wins: 0, losses: 0, draws: 0 };
    
    const updates = {
      [`stats/${result}s`]: (currentStats[`${result}s`] || 0) + 1,
      dateUpdated: Date.now(),
    };

    await update(competitionRef, updates);
  } catch (error) {
    logger.error('Error updating competition stats:', error);
  }
};

/**
 * Gets competition statistics for a user
 * @param uid User ID
 * @returns Competition statistics
 */
export const getUserCompetitionStats = async (uid?: string): Promise<{
  totalCompetitions: number;
  totalMatches: number;
  wins: number;
  losses: number;
  draws: number;
  medals: {
    gold: number;
    silver: number;
    bronze: number;
  };
}> => {
  try {
    const competitions = await getUserCompetitions(uid);
    
    const stats = {
      totalCompetitions: competitions.length,
      totalMatches: 0,
      wins: 0,
      losses: 0,
      draws: 0,
      medals: {
        gold: 0,
        silver: 0,
        bronze: 0,
      }
    };

    // Aggregate stats from competitions
    competitions.forEach(comp => {
      // Count medals based on rank (original app structure)
      if (comp.rank === 1) {
        stats.medals.gold += 1;
      } else if (comp.rank === 2) {
        stats.medals.silver += 1;
      } else if (comp.rank === 3) {
        stats.medals.bronze += 1;
      }
      
      // Count wins/losses from original app fields
      if (comp.winsCount) {
        stats.wins += comp.winsCount;
      }
      if (comp.lossesCount) {
        stats.losses += comp.lossesCount;
      }
      
      stats.totalMatches += (comp.winsCount || 0) + (comp.lossesCount || 0);
    });

    return stats;
  } catch (error) {
    logger.error('Error getting competition stats:', error);
    throw error;
  }
};

/**
 * Add a match to a competition
 * @param competitionId Competition ID
 * @param matchData Match data
 * @returns Match ID
 */
export const addMatch = async (
  competitionId: string,
  matchData: {
    opponentName: string;
    result: 'win' | 'loss' | 'draw';
    method?: string;
    round?: number;
    notes?: string;
  }
): Promise<string> => {
  const user = getCurrentUser();
  if (!user) throw new Error('Must be logged in');
  
  const matchesRef = ref(database, `competitions/${competitionId}/matches`);
  const newMatchRef = push(matchesRef);
  const matchId = newMatchRef.key;

  if (!matchId) throw new Error('Failed to generate match ID');

  // Clean matchData to remove undefined values before writing to Firebase
  await set(newMatchRef, {
    ...cleanForFirebase(matchData),
    dateCreated: Date.now(),
    uid: user.uid
  });
  
  // Update competition winsCount/lossesCount (original app structure)
  const compRef = ref(database, `competitions/${competitionId}`);
  const compSnapshot = await get(compRef);
  
  if (compSnapshot.exists()) {
    const comp = compSnapshot.val();
    const updates: any = { dateUpdated: Date.now() };
    
    if (matchData.result === 'win') {
      updates.winsCount = (comp.winsCount || 0) + 1;
    } else if (matchData.result === 'loss') {
      updates.lossesCount = (comp.lossesCount || 0) + 1;
    }
    
    await update(compRef, updates);
  }
  
  return matchId;
};

/**
 * Get a specific match
 * @param competitionId Competition ID
 * @param matchId Match ID
 * @returns Match data
 */
export const getMatch = async (
  competitionId: string,
  matchId: string
): Promise<any> => {
  const matchRef = ref(database, `competitions/${competitionId}/matches/${matchId}`);
  const snapshot = await get(matchRef);
  
  if (!snapshot.exists()) {
    throw new Error('Match not found');
  }
  
  return {
    id: matchId,
    ...snapshot.val()
  };
};

/**
 * Update a match
 * @param competitionId Competition ID
 * @param matchId Match ID
 * @param matchData Updated match data
 */
export const updateMatch = async (
  competitionId: string,
  matchId: string,
  matchData: Partial<{
    opponentName: string;
    result: 'win' | 'loss' | 'draw';
    method?: string;
    round?: number;
    notes?: string;
  }>
): Promise<void> => {
  const user = getCurrentUser();
  if (!user) throw new Error('Must be logged in');
  
  const matchRef = ref(database, `competitions/${competitionId}/matches/${matchId}`);
  
  // Get current match to check ownership
  const snapshot = await get(matchRef);
  if (!snapshot.exists()) throw new Error('Match not found');
  
  const currentMatch = snapshot.val();
  if (currentMatch.uid !== user.uid) {
    throw new Error('You can only edit your own matches');
  }

  // Clean matchData to remove undefined values before writing to Firebase
  await update(matchRef, {
    ...cleanForFirebase(matchData),
    dateModified: Date.now()
  });
  
  // If result changed, update competition winsCount/lossesCount
  if (matchData.result && matchData.result !== currentMatch.result) {
    const compRef = ref(database, `competitions/${competitionId}`);
    const compSnapshot = await get(compRef);
    
    if (compSnapshot.exists()) {
      const comp = compSnapshot.val();
      const updates: any = { dateUpdated: Date.now() };
      
      // Update counts based on result changes
      if (currentMatch.result === 'win' && matchData.result !== 'win') {
        updates.winsCount = Math.max(0, (comp.winsCount || 0) - 1);
      } else if (currentMatch.result === 'loss' && matchData.result !== 'loss') {
        updates.lossesCount = Math.max(0, (comp.lossesCount || 0) - 1);
      }
      
      if (matchData.result === 'win' && currentMatch.result !== 'win') {
        updates.winsCount = (comp.winsCount || 0) + 1;
      } else if (matchData.result === 'loss' && currentMatch.result !== 'loss') {
        updates.lossesCount = (comp.lossesCount || 0) + 1;
      }
      
      await update(compRef, updates);
    }
  }
};

/**
 * Delete a match
 * @param competitionId Competition ID
 * @param matchId Match ID
 */
export const deleteMatch = async (
  competitionId: string,
  matchId: string
): Promise<void> => {
  const user = getCurrentUser();
  if (!user) throw new Error('Must be logged in');
  
  const matchRef = ref(database, `competitions/${competitionId}/matches/${matchId}`);
  
  // Get match to check ownership and update stats
  const snapshot = await get(matchRef);
  if (!snapshot.exists()) throw new Error('Match not found');
  
  const match = snapshot.val();
  if (match.uid !== user.uid) {
    throw new Error('You can only delete your own matches');
  }
  
  // Delete the match
  await remove(matchRef);
  
  // Update competition winsCount/lossesCount
  const compRef = ref(database, `competitions/${competitionId}`);
  const compSnapshot = await get(compRef);
  
  if (compSnapshot.exists()) {
    const comp = compSnapshot.val();
    const updates: any = { dateUpdated: Date.now() };
    
    if (match.result === 'win') {
      updates.winsCount = Math.max(0, (comp.winsCount || 0) - 1);
    } else if (match.result === 'loss') {
      updates.lossesCount = Math.max(0, (comp.lossesCount || 0) - 1);
    }
    
    await update(compRef, updates);
  }
};

/**
 * Get all matches for a competition
 * @param competitionId Competition ID
 * @returns Array of matches
 */
export const getCompetitionMatches = async (competitionId: string): Promise<any[]> => {
  const matchesRef = ref(database, `competitions/${competitionId}/matches`);
  const snapshot = await get(matchesRef);
  
  if (!snapshot.exists()) {
    return [];
  }
  
  const matches: any[] = [];
  snapshot.forEach((childSnapshot) => {
    matches.push({
      id: childSnapshot.key!,
      ...childSnapshot.val()
    });
  });
  
  return matches.sort((a, b) => (b.dateCreated || 0) - (a.dateCreated || 0));
};

/**
 * Get all matches for a user across all competitions
 * @param userId User ID
 * @returns Array of matches with competition info
 */
export const getUserMatches = async (userId: string): Promise<any[]> => {
  // Get all competitions for the user
  const competitions = await getUserCompetitions(userId);
  const allMatches: any[] = [];
  
  // Get matches from each competition
  await Promise.all(
    competitions.map(async (comp) => {
      if (!comp.id) return; // Skip if no id
      
      const matches = await getCompetitionMatches(comp.id);
      // Add competition info to each match
      matches.forEach(match => {
        allMatches.push({
          ...match,
          competitionId: comp.id,
          competitionName: comp.tournamentName,
          competitionDate: comp.date || comp.dateCreated
        });
      });
    })
  );
  
  return allMatches.sort((a, b) => (b.dateCreated || 0) - (a.dateCreated || 0));
};